import * as CLIENT_VERSION from '../../../VERSION'
import FIREBASE_CONFIG from '../../../config/firebase-config'
import NAKAMA_CONFIG from '../../../config/nakama-config'

import React from 'react'
import { cloneDeep } from 'lodash'

import Board from '../../board/Board'
import Player from '../../player/Player'
import Clock from '../../clock/Clock'
import GameHeader from './gameHeader/GameHeader'
import GamePopup from './gamePopup/GamePopup'
import GameControls from './gameControls/GameControls'
import GameMessages from './gameMessages/GameMessages'
import GameConnection from './gameConnection/GameConnection'

import './gameView.scss'

import {
  WHITE,
  BLACK,
  WHITE_KING,
  BLACK_KING,
  BOARD_BASE_SIZE,
} from '../../../common/consts'
import Connector, { API_ERRORS } from '../../../api-client/Connector'
import BoardGameProtocolAdapter, {
  COLOR,
  CLOCK_FREEZE_REASON,
} from '../../../api-client/protocol-adapters/BoardGameProtocolAdapter'
import Move from './Move'

import AppContext from '../../../context/AppContext'

import { BoardAnimation } from '../../../common/BoardAnimation'
import Settings from '../../../Settings'
import SoundManager, {
  BECOME_KING_SOUND,
  CAPTURE_SOUND,
  MOVE_SOUND,
  START_GAME_SOUND,
  TIME_RUNNING_OUT,
} from '../../soundManager/SoundManager'
import POPUP from '../../../constants/popup'
import PIECE_COUNT from '../../../constants/pieceCount'
import { resizeBoard } from "./utils/resizeBoard";
import ReactDOM from "react-dom";

const _piece = (v) => {
  return {
    0: { 0: '?', 1: '?', 2: '?' },
    2: { 0: WHITE, 1: WHITE_KING },
    3: { 0: BLACK, 1: BLACK_KING },
  }[v.color][v.kind]
}

Settings.define('game', {
  coords: true,
  sounds: true,
  highlightMovable: true,
  showLastMove: true,
  showCaptured: false,
  showAvailableSquares: true,
  switchDarkMode: false,
})

export default class GameView extends React.Component {
  static contextType = AppContext

  constructor() {
    super()

    const settings = Settings.get('game').read()

    this.state = {
      board: {
        moves: [],
        fields: null,
        rotated: false,
        size: 8,
        disabled: false,
        whiteCaptured: 0,
        blackCaptured: 0,
      },
      player: {},
      eloPlayer: null,
      opponent: {},
      eloOpponent: null,
      clock: {},
      draw: {},
      game: {
        active: false,
      },
      popup: {},
      boardSizePx: '300px',
      boardSized: false,
      room: {},
      tournament: {},
      settings,
    }

    this._state = { ...this.state } //itermediate state

    this._playAreaContentRef = React.createRef()
    this._playAreaRef = React.createRef()
    this._sideAreaRef = React.createRef()
    this._boardRef = React.createRef()

    SoundManager.initialize()
    SoundManager.setEnabled(settings.sounds)
  }

  setState(state) {
    this.setIntermediateState(state)
    super.setState(state)
  }

  setIntermediateState(state, key) {
    if (key) {
      this._state[key] = Object.assign(this._state[key], state)
    } else {
      this._state = Object.assign(this._state, state)
    }
  }

  commitState() {
    this.setState(cloneDeep(this._state))
  }

  componentDidMount() {
    resizeBoard()
    this._joinGame()

    window.addEventListener('resize', this._resizeListener)
    window.addEventListener('beforeunload', this._beforeUnloadListener, {
      capture: true,
    })

    Settings.get('game').onChange(this._settingsChangeHandler)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._resizeListener)
    window.removeEventListener('beforeunload', this._beforeUnloadListener, {
      capture: true,
    })

    window.onresize = null
    this._connector.disconnect()

    Settings.get('game').removeOnChange(this._settingsChangeHandler)
  }

  _settingsChangeHandler = function (data) {
    this.setState({ settings: data })
    SoundManager.setEnabled(data.sounds)
  }.bind(this)

  _resizeListener = function () {
    resizeBoard()
  }.bind(this)

  _beforeUnloadListener = function (event) {
    if (this._isGameActive()) {
      event.preventDefault()
      return (event.returnValue =
        'You are about leaving active game. Are you sure?')
    }
  }.bind(this)

  async _joinGame() {
    this._openPopup(POPUP.AWAITING_OPPONENT)

    if (this._connector) {
      this._connector.disconnect()
    }

    this._setupConnector()

    await this._connector.init()
    const room = await this._connector.getRoomInfo(this.props.roomId)

    this.setState({ room: room, tournament: this.props.tournament});
    this._connector.join(this.props.roomId, this.props.level, this.props.tournament.id);
  }

  _isMyTurn() {
    return this._state.sideToMove === this._state.game.yourColor
  }

  _isMyColor(color) {
    return this._state.game.yourColor === color
  }

  _isUserAnonymous() {
    return this._connector && this._connector.isAnonymous()
  }

  _setupConnector() {
    let animation = null

    this._connector = new Connector(
      FIREBASE_CONFIG,
      NAKAMA_CONFIG,
      CLIENT_VERSION,
      BoardGameProtocolAdapter,
      {
        setGameState: (params) => {
          console.log('OnlineBoard::connector::setGameState', params)
          this.setIntermediateState(params, 'game')
        },
        initBoard: (params) => {
          console.log('OnlineBoard::connector::initBoard', params)
          this._initializedSide = false

          const board = {
            fields: {},
            firstFieldWhite: params.zeroFieldColor === COLOR.WHITE,
            playOnWhite:
              (params.zeroFieldColor === COLOR.WHITE &&
                params.playableFieldsIndexes.indexOf(0) > -1) ||
              (params.zeroFieldColor === COLOR.BLACK &&
                params.playableFieldsIndexes.indexOf(1) > -1),
            size: Math.sqrt(params.boardId),
            rotated: this._state.game.yourColor === COLOR.BLACK,
            disabled: true,
            moves: [],
            whiteCaptured: 0,
            blackCaptured: 0,
          }

          params.pieces.forEach((piece) => {
            board.fields[piece.fieldIndex] = _piece(piece.piece)
          })

          this.setIntermediateState({ board })
        },
        activateGame: () => {
          console.log('OnlineBoard::connector::activateGame')
          this.setIntermediateState({ active: true }, 'game')
          this.setIntermediateState({ popup: { type: null, data: null } })

          SoundManager.play(START_GAME_SOUND)
        },
        setSideToMove: (side) => {
          this.setIntermediateState({ sideToMove: side })
          this.setIntermediateState({ moves: [] }, 'board')
        },
        setAvailableMoves: (moves) => {
          this.setIntermediateState(
            {
              moves: Object.entries(moves).map(
                ([id, move]) => new Move(id, move)
              ),
            },
            'board'
          )
        },
        _beforeMove: () => {
          this.setIntermediateState({ moves: [] }, 'board')
          this.commitState()

          animation = new BoardAnimation()
        },
        jumps: (jumps) => {
          animation.addMove(jumps)
        },
        capture: ({ fieldIndex }) => {
          if (fieldIndex) {
            const fields = this._boardInterface.getActualFields()
            const key =
              fields[fieldIndex].toLowerCase() === WHITE
                ? 'whiteCaptured'
                : 'blackCaptured'
            this.setIntermediateState(
              { [key]: this._state.board[key] + 1 },
              'board'
            )
          }

          animation.addCapture(fieldIndex, () =>
            SoundManager.play(CAPTURE_SOUND)
          )
        },
        replace: (replace) => {
          animation.addPromote(
            replace.fieldIndex,
            _piece(replace.newPiece),
            () => {
              SoundManager.stop(CAPTURE_SOUND)
              SoundManager.stop(MOVE_SOUND)
              SoundManager.play(BECOME_KING_SOUND)
            }
          )
        },
        _afterMove: async () => {
          animation.addFinish(() => {
            if (!SoundManager.isPlaying(CAPTURE_SOUND)) {
              SoundManager.play(MOVE_SOUND)
            }
          })
          return this._boardInterface.animate(animation)
        },
        add: (piece) => {
          return this._boardInterface.addPiece(
            Object.keys(piece)[0],
            _piece(Object.values(piece)[0])
          )
        },
        setAnonymousPlayer: (player) => {
          player.name = (
            'player' + Math.round(Math.random() * Date.now())
          ).substring(0, 12)
          player.avatarId = Math.round(Math.random()) + 1
          player.anonymous = true

          console.warn('Connector::setAnonymousePlayer', player)

          if (this._isMyColor(player.playerColor)) {
            this.setIntermediateState({ player: player })
          } else {
            this.setIntermediateState({ opponent: player })
          }
        },
        setFirebasePlayer: (player) => {
          console.log('Connector::setFirebasePlayer', player)

          if (this._isMyColor(player.playerColor)) {
            this.setIntermediateState({ player: player })
          } else {
            this.setIntermediateState({ opponent: player })
          }
        },
        setPlayerElo: (player) => {
          if (this._isMyColor(player.playerColor)) {
            this.setIntermediateState({
              eloPlayer: player.elo
            })
          } else {
            this.setIntermediateState({
              eloOpponent: player.elo
            })
          }
        },
        setPlayerClockState: (clock) => {
          console.log('Connector::setPlayerClockState', clock)

          this.setIntermediateState({ [clock.playerColor]: clock }, 'clock')
        },
        setDrawState: (drawState) => {
          console.log('Connector::setDrawState', drawState)

          this.setIntermediateState({ draw: drawState })
        },
        completeGame: (result) => {
          console.log('Connector::completeGame', result)

          this.setIntermediateState({ disabled: true }, 'board')
          this.setIntermediateState({ active: false, result: result }, 'game')
          this.setIntermediateState({
            popup: {
              type: POPUP.GAME_OVER_POPUP,
              data: result,
            },
          })

          this._connector.refreshUserSessionData()
        },
        showSummary: (value) => {
          console.log('Connector::showSummary', value)
          // @todo: handle this packet correctly.
          // It seems it arrives for onboarding as well, but in that case contains different structure
          // and/or different value for `kind` field.
          if (value.itemsList && value.itemsList[0] && value.itemsList[0].eloProgress && value.itemsList[0].eloProgress.elo) {
            this.setIntermediateState({ ['summaryElo']: value.itemsList[0].eloProgress.elo.currentValue }, 'player')
            this.setIntermediateState({ ['differenceElo']: value.itemsList[0].eloProgress.elo.valueChange }, 'player')
          }
        },
        apiError: (error) => {
          console.error('Connector::apiError', error)
          switch (error.type) {
            case API_ERRORS.JOIN_MATCH_ERROR:
            case API_ERRORS.MAKE_MATCH_ERROR:
              this._openPopup(POPUP.AWAITING_OPPONENT, { notFound: true })
              break
            default:
              this._openPopup(POPUP.ERROR_POPUP, {
                title: 'API error',
                message: `Sorry, something went wrong (${error.type})`,
              })
          }
        },
        protocolError: (error) => {
          console.error('Connector::protocolError', error)

          // dont show 401 errors
          if (error.code !== 401) {
            this.setIntermediateState({
              popup: {
                type: POPUP.ERROR_POPUP,
                data: {
                  title: 'Protocol error',
                  message: `${error.message} (${error.code})`,
                },
              },
            })
          }
        },

        _queueStart: () => {
          this.setIntermediateState({ disabled: true }, 'board')

          this.commitState()
        },
        _queueComplete: () => {
          this._connector.confirmReady()

          this.setIntermediateState({ disabled: false }, 'board')

          this.commitState()
        },
      }
    )
  }

  _onBoardUserMove(move) {
    this._connector.movePiece(move)
  }

  _onBoardInterfacePublish(boardInterface) {
    this._boardInterface = boardInterface
  }

  // Popups

  _openPopup(popupId, data) {
    this.setState({ popup: { type: popupId, data: data } })
  }

  _popupDidClose() {
    this.setState({ popup: { type: null, data: null } })
  }

  _isGameFinished() {
    return !!this.state.game.result
  }

  _isGameActive() {
    return this.state.game.active
  }

  _canAcceptOrRejectDraw() {
    return this._isGameActive() && this.state.draw.canAcceptOrRejectDraw
  }

  _isDrawRejected() {
    return (
      this._isGameActive() && this.state.draw.shouldInformAboutProposalRejection
    )
  }

  _isOpponentDisconnected() {
    return (
      this._clockState(this.state.opponent.playerColor).freezeReason ===
      CLOCK_FREEZE_REASON.DISCONNECTED
    )
  }

  _isDisabled() {
    return !!this.state.game.result || this._isOpponentDisconnected()
  }

  _canProposeDraw() {
    return (
      this._isGameActive() && this.state.draw.canProposeDraw && this._isMyTurn()
    )
  }

  _canSurrender() {
    return this._isGameActive()
  }

  _newGame() {
    SoundManager.stopAll()
    if (this._connector.isAnonymous()) {
      this.props.switchView.main()
    } else {
      this.context.app.exit()
    }
  }

  _newArenaGame() {
    SoundManager.stopAll()
    document.body.classList.remove('in-game')
    ReactDOM.unmountComponentAtNode(document.getElementById('root'))
    window.__runApp(this.state.room.id, "game", {"id": this.state.tournament.id})
  }

  _exitGame() {
    if (
      !this._isGameActive() ||
      window.confirm('You are about to leave the game. Are you sure?')
    ) {
      SoundManager.stopAll()
      this.context.app.exit()
    }
  }

  _clockState(playerColor) {
    const clock = this.state.clock[playerColor] || {}
    clock.freeze = clock.freeze || !this._isGameActive()
    clock.visible = clock.visible && this._isGameActive()

    return clock
  }

  _getPiecesCaptured(playerColor) {
    return playerColor === COLOR.WHITE
      ? this.state.board.blackCaptured
      : this.state.board.whiteCaptured
  }

  _getBoardSize(boardSettings) {
    return boardSettings.rules.boardSize !== undefined
      ? boardSettings.rules.boardSize
      : this.state.board.size
  }

  _isAllPiecesCaptured(boardSettings) {
    const allPieces = PIECE_COUNT[this._getBoardSize(boardSettings)]
    return (
      allPieces ===
      Math.max(this.state.board.blackCaptured, this.state.board.whiteCaptured)
    )
  }

  render() {
    const rules = Object.assign(this.props.rules, {
      time: this.state.room.game
        ? this.state.room.game.modifiers.find(
          (modifier) => modifier.id === 1 || modifier.id === 2
        ).params
        : null,
    })

    const dragTargets = {}

    this.state.board.moves.forEach((move) => {
      const start = move.start()
      const end = move.end()

      if (!dragTargets[start]) {
        dragTargets[start] = [end]
      } else {
        dragTargets[start].push(end)
      }
    })

    const boardSettings = this.context.app.data.rooms.find(
      (room) => room.roomId === this.props.roomId
    )

    return (
      <div className="game-view">
        <GameHeader
          clickHandler={() => this._exitGame()}
        />
        <GamePopup
          type={this.state.popup.type}
          data={this.state.popup.data}
          playerColor={this._state.game.yourColor}
          signInPage={this.context.app.data.signInPage}
          isAnonymous={this._isUserAnonymous()}
          onRetry={() => this._joinGame()}
          onClose={() => this._popupDidClose()}
          onProposeDraw={() =>
            this._canProposeDraw() ? this._connector.proposeDraw() : null
          }
          isAllPiecesCaptured={this._isAllPiecesCaptured(boardSettings)}
          onSurrender={() =>
            this._canSurrender() ? this._connector.surrender() : null
          }
          onNewGame={() => this._newGame()}
          onNewArenaGame={() => this._newArenaGame()}
          player={this.state.player}
          gameIcon={this.props.rules.icon}
          tournament={this.state.tournament}
        />

        <div className="wrapper">
          <div className="container">
            <div className="play-area">
              <div className="overboard">
                <div className="player-info">
                  {this._isOpponentDisconnected() && <GameConnection />}
                  <Player
                    avatarId={this.state.opponent.avatarId}
                    countryCode={this.state.opponent.countryCode}
                    name={this.state.opponent.name}
                    hasTurn={!this._isMyTurn()}
                    visible={!!this.state.opponent.name}
                    isWhite={this.state.opponent.playerColor === COLOR.WHITE}
                    piecesCaptured={this._getPiecesCaptured(
                      this.state.opponent.playerColor
                    )}
                    isRed={
                      this._getBoardSize(boardSettings) === BOARD_BASE_SIZE
                    }
                    eloRating={this.state.eloOpponent}
                  />
                  <Clock
                    {...this._clockState(this.state.opponent.playerColor)}
                  />
                </div>
              </div>

              <div className="board-container">
                <div className="board-wrapper">
                  <Board
                    fields={this.state.board.fields}
                    firstFieldWhite={
                      boardSettings.rules.firstFieldWhite !== undefined
                        ? boardSettings.rules.firstFieldWhite
                        : this.state.board.firstFieldWhite
                    }
                    playOnWhite={
                      boardSettings.rules.playOnWhite !== undefined
                        ? boardSettings.rules.playOnWhite
                        : this.state.board.playOnWhite
                    }
                    size={this._getBoardSize(boardSettings)}
                    fieldAlphaNotation={boardSettings.rules.fieldAlphaNotation}
                    rotated={this.state.board.rotated}
                    moves={this.state.board.moves}
                    onInterfacePublish={this._onBoardInterfacePublish.bind(
                      this
                    )}
                    onUserMove={this._onBoardUserMove.bind(this)}
                    dragTargets={dragTargets}
                    showCoords={this.state.settings.coords}
                    playSounds={this.state.settings.sounds}
                    highlightMovable={this.state.settings.highlightMovable}
                    showLastMove={this.state.settings.showLastMove}
                    showCaptured={this.state.settings.showCaptured}
                    showAvailableSquares={this.state.settings.showAvailableSquares}
                    disabled={this.state.board.disabled || this._isDisabled()}
                  />
                </div>
              </div>
              <div className="underboard">
                <div className="player-info">
                  <Player
                    avatarId={this.state.player.avatarId}
                    countryCode={this.state.player.countryCode}
                    name={this.state.player.name}
                    hasTurn={this._isMyTurn()}
                    color={this.state.player.playerColor}
                    suffix="(You)"
                    visible={!!this.state.player.name}
                    isWhite={this.state.player.playerColor === COLOR.WHITE}
                    isRed={
                      this._getBoardSize(boardSettings) === BOARD_BASE_SIZE
                    }
                    piecesCaptured={this._getPiecesCaptured(
                      this.state.player.playerColor
                    )}
                    eloRating={this.state.eloPlayer}
                  />
                  <Clock
                    onTick={(leftSeconds) => {
                      if (leftSeconds === 19) {
                        SoundManager.play(TIME_RUNNING_OUT, false)
                      }
                    }}
                    {...this._clockState(this.state.player.playerColor)}
                  />
                </div>
                <GameMessages
                  canAcceptOrRejectDraw={this._canAcceptOrRejectDraw()}
                  isDrawRejected={this._isDrawRejected()}
                  acceptDrawProposal={() => this._connector.acceptDrawProposal()}
                  rejectDrawProposal={() => this._connector.rejectDrawProposal()}
                />
              </div>
            </div>

            <GameControls
              rules={rules}
              openPopup={(popupType, rules) => this._openPopup(popupType, rules)}
              newGame={() => this._newGame()}
              onNewArenaGame={() => this._newArenaGame()}
              isGameActive={this._isGameActive()}
              isUserAnonymous={this._isUserAnonymous()}
              isGameFinished={this._isGameFinished()}
              canProposeDraw={!this._canProposeDraw() || this._isDisabled()}
              canSurrender={!this._canSurrender() || this._isDisabled()}
              tournament={this.state.tournament}
            />
          </div>
        </div>
      </div>
    )
  }
}
